import {
  AboveGradeWallType,
  BelowGradeWallType,
  ExposureDirectionType,
  FootprintPolygonsType,
  PositionType,
  SpaceType
} from 'data/types';
import { useId, useMemo } from 'react';
import { assignWallsToPolygonEdges } from 'utils/polygon-exterior-walls';
import {
  EdgeCardinal,
  getExteriorPolygonEdges,
  getSpaceVertices,
  verticesToAbsolute
} from 'utils/space-polygon';
import {
  layoutPolygonWallOpenings,
  projectPolygonWallOpenings
} from 'utils/space-polygon-wall-openings';
import { getOrientedEdge } from 'utils/structure';
import SpacePolygonWallOpenings from './space-polygon-wall-openings';
import SpacePolygonWallsHint from './space-polygon-walls-hint';
import {
  getEdgeStrokeColor,
  getEdgeStrokeWidth,
  polygonCentroid,
  WALL_HIT_STROKE
} from './space-polygon-walls-helpers';

const VIEWBOX_WIDTH = 420;
const VIEWBOX_HEIGHT = 320;
const PADDING = 20;
const EXTERIOR_STROKE = 7;
const INTERIOR_STROKE = 1.5;
const SELECTED_STROKE = 9;

type SpacePolygonWallsProps = {
  space: Pick<SpaceType, 'vertices' | 'width' | 'length' | 'position'>;
  footprints: FootprintPolygonsType | null;
  walls: (AboveGradeWallType | BelowGradeWallType)[];
  homeOrientation: ExposureDirectionType | undefined;
  selectedWallId: string | null;
  onSelectWall: (wallId: string | null) => void;
};

type Layout = {
  vertices: PositionType[];
  scale: number;
  offsetX: number;
  offsetY: number;
};

function computeLayout(absoluteVertices: PositionType[]): Layout | null {
  if (absoluteVertices.length < 3) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const v of absoluteVertices) {
    if (v.x < minX) minX = v.x;
    if (v.x > maxX) maxX = v.x;
    if (v.y < minY) minY = v.y;
    if (v.y > maxY) maxY = v.y;
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;

  const availableWidth = VIEWBOX_WIDTH - PADDING * 2;
  const availableHeight = VIEWBOX_HEIGHT - PADDING * 2;
  const scale = Math.min(availableWidth / width, availableHeight / height);
  const offsetX = (VIEWBOX_WIDTH - width * scale) / 2 - minX * scale;
  const offsetY = (VIEWBOX_HEIGHT - height * scale) / 2 - minY * scale;

  return { vertices: absoluteVertices, scale, offsetX, offsetY };
}

function project(point: PositionType, layout: Layout): PositionType {
  return {
    x: point.x * layout.scale + layout.offsetX,
    y: point.y * layout.scale + layout.offsetY
  };
}

export default function SpacePolygonWalls({
  space,
  footprints,
  walls,
  homeOrientation,
  selectedWallId,
  onSelectWall
}: SpacePolygonWallsProps) {
  const reactId = useId();
  const hintClipId = `space-polygon-walls-hint-${reactId.replace(/:/g, '')}`;

  const absoluteVertices = useMemo(() => {
    const relative = getSpaceVertices(space);
    const origin = { x: space.position?.x ?? 0, y: space.position?.y ?? 0 };
    return verticesToAbsolute(relative, origin);
  }, [space]);

  const layout = useMemo(
    () => computeLayout(absoluteVertices),
    [absoluteVertices]
  );

  const edges = useMemo(
    () => getExteriorPolygonEdges(absoluteVertices, footprints),
    [absoluteVertices, footprints]
  );

  const wallsForEdges = useMemo(
    () =>
      assignWallsToPolygonEdges(
        edges,
        walls,
        (wall) => {
          if ('edge' in wall && wall.edge) return wall.edge;
          return getOrientedEdge(wall.exposure, homeOrientation) as
            | EdgeCardinal
            | undefined;
        },
        homeOrientation
      ),
    [edges, walls, homeOrientation]
  );

  const projectedOpenings = useMemo(() => {
    if (!layout) return [];
    return projectPolygonWallOpenings(
      layoutPolygonWallOpenings({
        edges,
        wallsForEdges
      }),
      (point) => project(point, layout)
    );
  }, [layout, edges, wallsForEdges]);

  const hintAnchor = useMemo(() => {
    if (!layout) return null;
    const projected = layout.vertices.map((vertex) => project(vertex, layout));
    return polygonCentroid(projected);
  }, [layout]);

  const hasSelectableWall = wallsForEdges.some((wall) => !!wall);

  if (!layout || edges.length === 0) {
    return (
      <div className='flex h-32 w-48 items-center justify-center rounded border-2 border-gray-300 bg-gray-50 text-sm text-gray-500'>
        Space
      </div>
    );
  }

  const polygonPoints = layout.vertices
    .map((v) => {
      const p = project(v, layout);
      return `${p.x},${p.y}`;
    })
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      className='w-full max-w-[460px] select-none overflow-visible'
      role='img'
      aria-label='Space exterior walls'
    >
      <defs>
        <clipPath id={hintClipId}>
          <polygon points={polygonPoints} />
        </clipPath>
      </defs>
      <polygon
        points={polygonPoints}
        fill='#ffffff'
        stroke='#e5e7eb'
        strokeWidth={INTERIOR_STROKE}
      />
      {edges.map((edge, edgeIndex) => {
        const wall = edge.isExterior ? wallsForEdges[edgeIndex] : null;
        const isSelected = !!wall && wall.uniqueId === selectedWallId;
        const a = project(edge.p1, layout);
        const b = project(edge.p2, layout);

        const stroke = getEdgeStrokeColor({
          isExterior: edge.isExterior,
          isSelected
        });
        const strokeWidth = getEdgeStrokeWidth({
          isExterior: edge.isExterior,
          isSelected,
          selectedStroke: SELECTED_STROKE,
          exteriorStroke: EXTERIOR_STROKE,
          interiorStroke: INTERIOR_STROKE
        });

        return (
          <line
            key={`edge-${edge.p1.x}-${edge.p1.y}-${edge.p2.x}-${edge.p2.y}`}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap='square'
          />
        );
      })}
      <SpacePolygonWallOpenings openings={projectedOpenings} />
      {hasSelectableWall && hintAnchor ? (
        <SpacePolygonWallsHint centroid={hintAnchor} clipPathId={hintClipId} />
      ) : null}
      {edges.map((edge, edgeIndex) => {
        const wall = edge.isExterior ? wallsForEdges[edgeIndex] : null;
        if (!wall) {
          return null;
        }
        const isSelected = wall.uniqueId === selectedWallId;
        const a = project(edge.p1, layout);
        const b = project(edge.p2, layout);
        return (
          <line
            key={`hit-${wall.uniqueId}-${edge.p1.x}-${edge.p1.y}`}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke='transparent'
            strokeWidth={WALL_HIT_STROKE}
            strokeLinecap='square'
            cursor='pointer'
            data-wall-hit={wall.uniqueId}
            onClick={() => onSelectWall(isSelected ? null : wall.uniqueId)}
          />
        );
      })}
    </svg>
  );
}
